import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Modal,
  Stack,
  Text,
  Radio,
  Textarea,
  Button,
  Group,
  Checkbox,
  Alert,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconMailCancel,
  IconMoodAngry,
  IconBan,
  IconEyeOff,
  IconInfoCircle,
  IconDots,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { moderationService } from '../../services/moderation';

type ReportType = 'Review' | 'Comment' | 'User';
type ReportReason =
  | 'Spam'
  | 'Harassment'
  | 'HateSpeech'
  | 'Inappropriate'
  | 'FalseInformation'
  | 'Other';

interface ReportModalProps {
  opened: boolean;
  onClose: () => void;
  targetId: string;
  targetType: ReportType;
  targetUserId?: string;
  targetName?: string;
}

const REPORT_REASONS: { value: ReportReason; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'Spam',
    label: 'Spam',
    description: 'Misleading or repetitive content',
    icon: <IconMailCancel size={18} />,
  },
  {
    value: 'Harassment',
    label: 'Harassment or Bullying',
    description: 'Targeting or intimidating behavior',
    icon: <IconMoodAngry size={18} />,
  },
  {
    value: 'HateSpeech',
    label: 'Hate Speech',
    description: 'Attacks based on identity or beliefs',
    icon: <IconBan size={18} />,
  },
  {
    value: 'Inappropriate',
    label: 'Inappropriate Content',
    description: 'Nudity, violence, or disturbing content',
    icon: <IconEyeOff size={18} />,
  },
  {
    value: 'FalseInformation',
    label: 'False Information',
    description: 'Misleading or inaccurate claims',
    icon: <IconInfoCircle size={18} />,
  },
  {
    value: 'Other',
    label: 'Other',
    description: 'Something else not listed above',
    icon: <IconDots size={18} />,
  },
];

export function ReportModal({
  opened,
  onClose,
  targetId,
  targetType,
  targetUserId,
  targetName,
}: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState<ReportReason | null>(null);
  const [description, setDescription] = useState('');
  const [blockUser, setBlockUser] = useState(false);

  const handleClose = () => {
    setSelectedReason(null);
    setDescription('');
    setBlockUser(false);
    onClose();
  };

  const reportMutation = useMutation({
    mutationFn: async () => {
      await moderationService.report({
        type: targetType,
        targetId,
        reason: selectedReason!,
        description: description.trim() || undefined,
      });
      if (blockUser && targetUserId) {
        await moderationService.blockUser(targetUserId);
      }
    },
    onSuccess: () => {
      notifications.show({
        title: 'Report submitted',
        message: blockUser
          ? 'Thank you for your report. The user has been blocked.'
          : 'Thank you for helping keep our community safe.',
        color: 'green',
      });
      handleClose();
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to submit report. Please try again.', color: 'red' });
    },
  });

  const handleSubmit = () => {
    if (!selectedReason) {
      notifications.show({ title: 'Select a reason', message: 'Please select a reason for your report', color: 'yellow' });
      return;
    }
    reportMutation.mutate();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconAlertTriangle size={20} />
          <Text fw={600}>Report {targetName || 'content'}</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Why are you reporting this {targetType.toLowerCase()}? Your report is anonymous.
        </Text>

        <Radio.Group
          value={selectedReason || ''}
          onChange={(value) => setSelectedReason(value as ReportReason)}
        >
          <Stack gap="xs">
            {REPORT_REASONS.map((reason) => (
              <Radio
                key={reason.value}
                value={reason.value}
                label={
                  <Group gap="xs">
                    {reason.icon}
                    <div>
                      <Text size="sm" fw={500}>
                        {reason.label}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {reason.description}
                      </Text>
                    </div>
                  </Group>
                }
                styles={{
                  radio: { cursor: 'pointer' },
                  label: { cursor: 'pointer', paddingLeft: 8 },
                }}
              />
            ))}
          </Stack>
        </Radio.Group>

        <Textarea
          label="Additional details (optional)"
          placeholder="Provide more context about this report..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={3}
          maxLength={500}
        />

        {targetUserId && (
          <>
            <Alert color="gray" variant="light">
              <Checkbox
                checked={blockUser}
                onChange={(e) => setBlockUser(e.currentTarget.checked)}
                label={
                  <div>
                    <Text size="sm" fw={500}>
                      Block this user
                    </Text>
                    <Text size="xs" c="dimmed">
                      You won't see their content and they won't be able to interact with you
                    </Text>
                  </div>
                }
              />
            </Alert>
          </>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose} disabled={reportMutation.isPending}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleSubmit}
            loading={reportMutation.isPending}
            disabled={!selectedReason}
          >
            Submit Report
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
